/**
 * CompactDdlEditor — the DDL arm of the Compact VQE popover. Replaces the
 * read-only summary card with the REAL Visual DDL editor for the kinds that
 * have an embedded body, powered by the same edit pipeline the retired inline
 * DDL toolbar used (`useCompactDdlEditContext`).
 *
 * Layout (footer pass): the editor owns the popover's whole area below the
 * header as a TWO-region flex column — a `min-h-0 flex-1` scrolling body
 * (`ScrollFade`) and ONE pinned action row that never scrolls. The arm's own
 * form buttons ([Cancel] [Save …]) are portalled into that row through
 * `FormActionsSlot`, so a long form can never hide `Edit as SQL` below the
 * fold and the popover never shows two competing action rows. The popover's
 * old bottom "Close" row is gone with it — the header × is the close
 * affordance (`CompactVqeContent`), and the pinned [Cancel] closes the popup
 * through `onCancel` (a successful commit deliberately does NOT — see that
 * prop's doc).
 *
 * Dispatch, in order:
 *  1. popover closed → the read-only `CompactDdlCard`, and the edit pipeline
 *     is never mounted (see the `open` gate below).
 *  2. no wired `editContext`, or an objectKind with no embedded body yet →
 *     the read-only `CompactDdlCard` (unchanged fallback; its own action row
 *     is suppressed because the shell already pins the shared one).
 *  3. otherwise → the arm body from `compactDdlBodyArm`.
 *
 * Testids: the host wrapper carries `compact-ddl-editor-host` +
 * `data-object-kind`. It deliberately does NOT reuse `compact-ddl-editor` —
 * the `table` arm's `TableEditor` owns that id on its own shell root, because
 * every per-facet testid the E2E suite drives is derived from it as a prefix
 * (`compact-ddl-editor-tab-columns`, `compact-ddl-editor-constraints-add-pk-*`,
 * …). Keeping the two distinct means `compact-ddl-editor` resolves to exactly
 * one node.
 *
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T3).
 */
import type { ParseOptions } from '../../../sql-grammar/model';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { DdlCardModel } from '../../visual-query-editor/compact/compactDdlCard';
import { CompactDdlCard } from '../../visual-query-editor/compact/sections/CompactDdlCard';
import { renderCompactDdlBodyArm } from './compactDdlBodyArm';
import { CompactDdlShell } from './CompactDdlShell';
import { useCompactDdlEditContext } from './useCompactDdlEditContext';
import { useStickyDdlStructure } from './useStickyDdlStructure';

// The pinned-row chrome — and the `compact-ddl-editor-host` /
// `vqe-compact-ddl-actions` / `vqe-compact-ddl-form-actions` testids that go
// with it — live in `CompactDdlShell`; `compact/index.ts` re-exports them from
// there.

export interface CompactDdlEditorProps {
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  readonly tabId: string;
  /** Full Monaco buffer text (the detection parse owns the statement split). */
  readonly query: string;
  readonly options: ParseOptions;
  /** Whether the hosting popover is open. */
  readonly open: boolean;
  readonly connectionId?: string | null;
  readonly database?: string | null;
  /** Read-only model — the fallback card AND the footer's Design-table seed. */
  readonly ddlCard: DdlCardModel;
  readonly onEditAsSql: () => void;
  readonly onDesignTable: () => void;
  /**
   * The form arms' [Cancel] — "the user backed out", so the host closes the
   * popover. NOT wired to a successful commit: that funnels through the
   * app-shell PreviewDialog over this editor's live edit context, which must
   * stay mounted until the user applies it. Only the form arms have a Cancel;
   * the table and drop arms ignore it.
   */
  readonly onCancel: () => void;
}

export function CompactDdlEditor(props: CompactDdlEditorProps) {
  // A closed popover must not mount the resolver-backed edit pipeline at all
  // (no service, no parse, no cursor polling). Gating at the COMPONENT
  // boundary is what makes that guarantee — a prop-level flag could not, since
  // hooks run before any early return.
  if (!props.open) return <CompactDdlEditorFallback {...props} />;
  return <CompactDdlEditorBody {...props} />;
}

function CompactDdlEditorFallback({
  ddlCard,
  onEditAsSql,
  onDesignTable,
  connectionId,
}: CompactDdlEditorProps) {
  return (
    <CompactDdlShell
      designTable={ddlCard.designTable}
      connectionId={connectionId}
      onDesignTable={onDesignTable}
      onEditAsSql={onEditAsSql}
    >
      {/* `hideActions` — the shell already pins the shared action row, and a
          second copy would double the `vqe-ddl-*` testids. */}
      <CompactDdlCard model={ddlCard} onEditAsSql={onEditAsSql} hideActions />
    </CompactDdlShell>
  );
}

function CompactDdlEditorBody(props: CompactDdlEditorProps) {
  const { editorRef, tabId, query, options, connectionId, database, ddlCard, onEditAsSql, onCancel } =
    props;

  const { editContext, objectKind, ddlKind } = useCompactDdlEditContext({
    editorRef,
    tabId,
    query,
    options,
    enabled: true,
    connectionId,
    database,
  });

  // Resolved ONCE per render, above the dispatch, so the structure survives
  // the resolver's async re-parse window (the detection parse mints the new
  // statementId synchronously; `onTextChange` lands a tick later). Without it
  // every keystroke unmounted the table body and reset its tab to Columns.
  const structure = useStickyDdlStructure(editContext);

  const arm =
    editContext && objectKind
      ? renderCompactDdlBodyArm(objectKind, {
          editContext,
          ddlKind,
          onEditAsSql,
          onCancel,
          structure,
        })
      : null;

  if (!arm) return <CompactDdlEditorFallback {...props} />;

  // The form arms own a real primary (`Save changes`) in the same pinned row,
  // so Design table steps down to `outline` there; the table + drop arms have
  // no competing primary and keep it solid.
  const armOwnsPrimary = objectKind !== 'table' && objectKind !== 'drop';

  return (
    <CompactDdlShell
      objectKind={objectKind}
      designTable={ddlCard.designTable}
      connectionId={connectionId}
      onDesignTable={props.onDesignTable}
      onEditAsSql={onEditAsSql}
      ownsPrimary={!armOwnsPrimary}
    >
      {arm}
    </CompactDdlShell>
  );
}
